/**
 * Pure account limit + pace logic for the Roost wallboard (WI 34272): the
 * burn-rate projection and the "near limit / on pace to fill" rules.
 */
import type {
  AccountRollup,
  WindowSummary,
} from '../models/flock-telemetry.models';
import type { SeatWorkQueue } from '../models/work-queue.model';
import { parseUtcMs } from '../models/flock-telemetry.utils';

// ── Thresholds (Tim, WI 34272) ──────────────────────────────────────────────

/** "Accounts about to hit a 5 hour or 7 day limit." */
export const NEAR_LIMIT_PCT = 90;
/** "Above 50% 5hr or 7day and on pace to fill up before expiration." */
export const PACE_FLOOR_PCT = 50;
export const FIVE_HOUR_MS = 5 * 60 * 60_000;
export const WEEK_MS = 7 * 24 * 60 * 60_000;
/** Bug 34652 (Tim): act on a 5h or 7d window only when it is on pace for at
 * least this much at window end. 80-100% at reset is the healthy cadence. */
export const ACT_ON_PROJECTED_PCT = 95;
/**
 * A recent-burn slope needs at least this much time between its oldest and
 * newest samples, per window (Tim, Bug 34814). Quota readings move in whole
 * percent, so a short span turns one tick into a steep slope: 1% over 8 min
 * projected a 7d window from 24% to 887%. Below the span the projection uses
 * the window average.
 */
export const RECENT_BURN_MIN_SPAN_MS: Record<WindowKey, number> = {
  '5h': 30 * 60_000,
  '7d': 12 * 60 * 60_000,
};
/**
 * The window average divides by at least this much elapsed time (Bug 34836):
 * 3% used 2 min into a 5h window projected 450% and rated crit. The same span
 * as `RECENT_BURN_MIN_SPAN_MS`. Early in a window the true elapsed time is
 * shorter, so the clamped rate is a lower bound on the real burn: early noise
 * reads low, and a burn that fills even at that rate still reads crit.
 */
export const WINDOW_AVERAGE_MIN_ELAPSED_MS: Readonly<
  Record<WindowKey, number>
> = RECENT_BURN_MIN_SPAN_MS;
/** Recent-burn lookback per window; each holds its minimum span with room. */
export const RECENT_BURN_LOOKBACK_MS: Record<WindowKey, number> = {
  '5h': 60 * 60_000,
  '7d': 24 * 60 * 60_000,
};
/** Pace samples kept per account window, spread across its lookback. */
export const MAX_PACE_SAMPLES = 48;

// ── Pace projection ─────────────────────────────────────────────────────────

export interface PaceSample {
  atMs: number;
  pct: number;
}

export type PaceBasis = 'recent' | 'window-average' | 'none';

export interface PaceProjection {
  /** Burn rate in percentage points per hour; null when it cannot be derived. */
  burnPctPerHour: number | null;
  /** Percent the window reaches at reset if the burn rate holds. */
  projectedAtReset: number | null;
  /** When the window hits 100% at the current burn; null if it never does. */
  fillAtMs: number | null;
  /** True when the projected fill lands at or before the reset. */
  fillsBeforeReset: boolean;
  basis: PaceBasis;
  /** Span of the samples a `recent` rate came from; null otherwise (Bug 34688). */
  recentSpanMs: number | null;
}

/** The rate and the window it was measured over (Bug 34688 AC 1 and AC 4), so
 * a reader can check a projection's basis instead of trusting the number. */
export function rateBasisText(
  p: Pick<PaceProjection, 'basis' | 'burnPctPerHour' | 'recentSpanMs'>
): string {
  if (p.basis === 'none' || p.burnPctPerHour === null)
    return 'no burn rate yet';
  const r = Math.round(p.burnPctPerHour * 100) / 100;
  const rate = `rate ${r < 1 ? r.toFixed(2) : r.toFixed(1)} pts/h`;
  if (p.basis !== 'recent') return `${rate}, average since the window opened`;
  const span = p.recentSpanMs ?? 0;
  const over =
    span >= 2 * 3_600_000
      ? `${Math.round(span / 3_600_000)}h`
      : `${Math.round(span / 60_000)} min`;
  return `${rate} over the last ${over}`;
}

const NO_PACE: PaceProjection = {
  burnPctPerHour: null,
  projectedAtReset: null,
  fillAtMs: null,
  fillsBeforeReset: false,
  recentSpanMs: null,
  basis: 'none',
};

/**
 * Percent a window reaches at its end if the average burn so far holds:
 * used% x window length / time elapsed. The ONE projection the dashboard
 * warnings and the Seats / Accounts / Wallboard columns share (Bug 34652,
 * Bug 34656), so they never disagree. Elapsed time is clamped up to
 * `WINDOW_AVERAGE_MIN_ELAPSED_MS` (Bug 34836), and a clamped projection is
 * used% plus that rate over the time left; a window already at 100% inside
 * that span reads its current percent. Null for an elapsed time outside the
 * window, or under a minute of history.
 */
export function projectedPctAtWindowEnd(
  usedPct: number,
  elapsedMs: number,
  windowMs: number
): number | null {
  if (elapsedMs < 0 || elapsedMs > windowMs) return null;
  const averagedMs = windowAverageElapsedMs(elapsedMs, windowMs);
  if (averagedMs > elapsedMs && usedPct >= 100) return usedPct;
  // Under a minute is too little history for a pace (the reading may still
  // be last window's). A full reading is checked first on purpose: AC 2 says
  // full reads crit at any elapsed time, as projectWindow already does.
  if (elapsedMs < 60_000) return null;
  const used = Math.max(0, usedPct);
  if (averagedMs === elapsedMs) return (used * windowMs) / elapsedMs;
  // Clamped: used now plus the clamped rate over the TRUE time left, the same
  // formula projectPace's fill time uses, so projected >= 100 exactly when the
  // window fills before reset (grok_code-ryn, PR 4429 thread 54833).
  return used + (used * (windowMs - elapsedMs)) / averagedMs;
}

/** Elapsed time the window average divides by: at least the floor. */
function windowAverageElapsedMs(elapsedMs: number, windowMs: number): number {
  return Math.max(
    elapsedMs,
    WINDOW_AVERAGE_MIN_ELAPSED_MS[windowKeyOf(windowMs)]
  );
}

function windowKeyOf(windowMs: number): WindowKey {
  return windowMs === WEEK_MS ? '7d' : '5h';
}

/**
 * Act-on severity for a projected window (Bug 34652): critical when it fills
 * before reset, a warning at `ACT_ON_PROJECTED_PCT`+ projected, otherwise
 * none. Current usage alone never qualifies: no projection, no level.
 */
export function actOnLevel(
  projectedPct: number | null,
  fillsBeforeReset: boolean
): 'crit' | 'warn' | null {
  if (fillsBeforeReset) return 'crit';
  return projectedPct !== null && projectedPct >= ACT_ON_PROJECTED_PCT
    ? 'warn'
    : null;
}

/**
 * Project a usage window forward from its burn rate.
 *
 * Burn rate, in order of preference:
 *  1. `recent`: the slope between the oldest and newest samples of THIS window
 *     inside its lookback (`RECENT_BURN_LOOKBACK_MS`; samples from before the
 *     window opened are last window's). Needs 2+ samples at least
 *     `RECENT_BURN_MIN_SPAN_MS` apart (30 min for 5h, 12 h for 7d) and a
 *     non-negative slope; a drop means the window reset, so it is ignored.
 *  2. `window-average`: percent used divided by time elapsed since the window
 *     opened (window start = reset - window length, which holds for both the
 *     5h window, anchored on its first message, and the fixed 7d window).
 *
 * Projected at reset = percent + rate x time-to-reset.
 * Fill time = now + (100 - percent) / rate. "On pace" = fill <= reset.
 */
export function projectPace(
  percent: number,
  resetAtMs: number | null,
  windowMs: number,
  nowMs: number,
  recent: PaceSample[] = []
): PaceProjection {
  if (resetAtMs === null || resetAtMs <= nowMs) return NO_PACE;
  const remainingMs = resetAtMs - nowMs;

  let ratePerMs: number | null = null;
  let basis: PaceBasis = 'none';
  let recentSpanMs: number | null = null;

  const key = windowKeyOf(windowMs);
  const windowStartMs = resetAtMs - windowMs;
  const inLookback = recent
    .filter(
      (s) =>
        s.atMs <= nowMs &&
        s.atMs >= windowStartMs &&
        nowMs - s.atMs <= RECENT_BURN_LOOKBACK_MS[key]
    )
    .sort((a, b) => a.atMs - b.atMs);
  if (inLookback.length >= 2) {
    const first = inLookback[0];
    const last = inLookback[inLookback.length - 1];
    const span = last.atMs - first.atMs;
    if (span >= RECENT_BURN_MIN_SPAN_MS[key] && last.pct >= first.pct) {
      ratePerMs = (last.pct - first.pct) / span;
      basis = 'recent';
      recentSpanMs = span;
    }
  }

  let windowEndPct: number | null = null;
  if (ratePerMs === null) {
    const elapsedMs = nowMs - (resetAtMs - windowMs);
    windowEndPct = projectedPctAtWindowEnd(percent, elapsedMs, windowMs);
    if (windowEndPct !== null) {
      ratePerMs =
        Math.max(0, percent) / windowAverageElapsedMs(elapsedMs, windowMs);
      basis = 'window-average';
    }
  }

  if (ratePerMs === null) return NO_PACE;

  const projectedAtReset = windowEndPct ?? percent + ratePerMs * remainingMs;
  let fillAtMs: number | null = null;
  if (percent >= 100) fillAtMs = nowMs;
  else if (ratePerMs > 0) fillAtMs = nowMs + (100 - percent) / ratePerMs;

  return {
    burnPctPerHour: ratePerMs * 3_600_000,
    projectedAtReset,
    fillAtMs,
    fillsBeforeReset: fillAtMs !== null && fillAtMs <= resetAtMs,
    basis,
    recentSpanMs,
  };
}

/**
 * Bounded sample history for one account window (Bug 34814): drops samples
 * older than the window's lookback, then keeps the first sample of each
 * lookback / `MAX_PACE_SAMPLES` bucket plus the newest, so the history spans
 * the whole lookback (and can meet the minimum span) at any sample cadence.
 */
export function retainPaceSamples(
  list: readonly PaceSample[],
  window: WindowKey,
  nowMs: number
): PaceSample[] {
  const lookbackMs = RECENT_BURN_LOOKBACK_MS[window];
  const bucketMs = lookbackMs / MAX_PACE_SAMPLES;
  const live = list
    .filter((s) => nowMs - s.atMs <= lookbackMs)
    .sort((a, b) => a.atMs - b.atMs);
  const kept: PaceSample[] = [];
  let lastBucket: number | null = null;
  for (const s of live) {
    const bucket = Math.floor(s.atMs / bucketMs);
    if (bucket !== lastBucket) kept.push(s);
    lastBucket = bucket;
  }
  const newest = live[live.length - 1];
  if (newest && kept[kept.length - 1] !== newest) kept.push(newest);
  return kept;
}

// ── Account limit + pace alerts ─────────────────────────────────────────────

export type WindowKey = '5h' | '7d';
export type AccountAlertKind = 'near-limit' | 'on-pace';

export interface WindowAlert {
  window: WindowKey;
  percent: number;
  resetAtMs: number;
  nearLimit: boolean;
  onPace: boolean;
  pace: PaceProjection;
}

export interface AccountAlert {
  accountUpn: string;
  kind: AccountAlertKind;
  /** Windows that tripped a rule, near-limit first. */
  windows: WindowAlert[];
  /** Highest percent across the tripped windows. */
  peakPercent: number;
  /** Earliest projected fill across the tripped windows (null if none). */
  earliestFillAtMs: number | null;
  /** Live seats riding this account. */
  seats: string[];
}

/** Keyed `${accountUpnLower}|${window}`. */
export type PaceSampleMap = Map<string, PaceSample[]>;

export function paceKey(accountUpn: string, window: WindowKey): string {
  return `${accountUpn.toLowerCase()}|${window}`;
}

function evaluateWindow(
  accountUpn: string,
  window: WindowKey,
  summary: WindowSummary,
  windowMs: number,
  nowMs: number,
  samples: PaceSampleMap
): WindowAlert | null {
  const resetAtMs = parseUtcMs(summary.effectiveResetAt);
  // No live window: nothing can fill, and a stale 0% is not a reading.
  if (resetAtMs === null || resetAtMs <= nowMs) return null;
  const percent = summary.percentUsed;
  const pace = projectPace(
    percent,
    resetAtMs,
    windowMs,
    nowMs,
    samples.get(paceKey(accountUpn, window)) ?? []
  );
  const nearLimit = percent >= NEAR_LIMIT_PCT;
  const onPace = percent > PACE_FLOOR_PCT && pace.fillsBeforeReset;
  if (!nearLimit && !onPace) return null;
  return { window, percent, resetAtMs, nearLimit, onPace, pace };
}

/**
 * Accounts near a limit, then accounts over 50% on pace to fill before reset.
 * Order: near-limit first (highest percent first), then on-pace (soonest
 * projected fill first). Accounts that trip neither rule are omitted.
 */
export function buildAccountAlerts(
  rollups: AccountRollup[],
  accountSeats: Map<string, string[]>,
  nowMs: number,
  samples: PaceSampleMap = new Map()
): AccountAlert[] {
  const alerts: AccountAlert[] = [];
  for (const r of rollups) {
    const windows = [
      evaluateWindow(
        r.accountUpn,
        '5h',
        r.fiveHour,
        FIVE_HOUR_MS,
        nowMs,
        samples
      ),
      evaluateWindow(r.accountUpn, '7d', r.weekly, WEEK_MS, nowMs, samples),
    ].filter((w): w is WindowAlert => w !== null);
    if (windows.length === 0) continue;
    windows.sort(
      (a, b) =>
        Number(b.nearLimit) - Number(a.nearLimit) || b.percent - a.percent
    );
    const fills = windows
      .map((w) => w.pace.fillAtMs)
      .filter((f): f is number => f !== null);
    alerts.push({
      accountUpn: r.accountUpn,
      kind: windows.some((w) => w.nearLimit) ? 'near-limit' : 'on-pace',
      windows,
      peakPercent: Math.max(...windows.map((w) => w.percent)),
      earliestFillAtMs: fills.length ? Math.min(...fills) : null,
      seats: accountSeats.get(r.accountUpn.toLowerCase()) ?? [],
    });
  }
  return alerts.sort(compareAccountAlerts);
}

export function compareAccountAlerts(a: AccountAlert, b: AccountAlert): number {
  if (a.kind !== b.kind) return a.kind === 'near-limit' ? -1 : 1;
  if (a.kind === 'near-limit') return b.peakPercent - a.peakPercent;
  const fa = a.earliestFillAtMs ?? Number.POSITIVE_INFINITY;
  const fb = b.earliestFillAtMs ?? Number.POSITIVE_INFINITY;
  return fa - fb || b.peakPercent - a.peakPercent;
}

/** Live (non-retired, non-dead) seats grouped by lower-cased account. */
export function seatsByAccount(seats: SeatWorkQueue[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const s of seats) {
    if (!s.accountUpn || s.retired || s.livenessBand === 'dead') continue;
    const key = s.accountUpn.toLowerCase();
    const list = map.get(key) ?? [];
    list.push(s.instanceId);
    map.set(key, list);
  }
  for (const list of map.values()) list.sort();
  return map;
}
